/* ════════════ VIOLET VILLAGE + PIXEL EVE ════════════
   The eve page's counterpart to the home page starfield: a quiet
   village at night — sparse stars, a blocky rooftop skyline whose
   windows flicker on and off (communities, hubs, lights in the dark),
   drifting fireflies — plus a 16×16 Eve machine on the hero HUD whose
   eye stays politely shut. */

import { onPage } from "./router";

const REDUCED = matchMedia("(prefers-reduced-motion: reduce)").matches;

/* ───────── atmosphere: stars, rooftops, fireflies ───────── */
function village(signal: AbortSignal) {
  const c = document.querySelector<HTMLCanvasElement>("#night")!;
  const ctx = c.getContext("2d")!;

  type Star = { x: number; y: number; s: number };
  type Fly = { x: number; y: number; vx: number; vy: number; phase: number; col: string };
  type Win = { x: number; y: number; lit: boolean; next: number };
  type House = { x: number; w: number; y: number };

  const flyColors = ["#b18cff", "#b18cff", "#e9defc", "#2bd9ff"];
  let stars: Star[] = [];
  let flies: Fly[] = [];
  let houses: House[] = [];
  let wins: Win[] = [];
  const STEP = 4; // window grid pitch inside a house — chunky on purpose

  const makeFly = (): Fly => ({
    x: Math.random() * c.width,
    y: c.height * (0.45 + Math.random() * 0.5),
    vx: (Math.random() - 0.5) * 0.3,
    vy: (Math.random() - 0.5) * 0.2,
    phase: Math.random() * Math.PI * 2,
    col: flyColors[(Math.random() * flyColors.length) | 0]!,
  });

  const seed = () => {
    c.width = innerWidth;
    c.height = innerHeight;
    stars = Array.from({ length: Math.min(80, Math.floor(c.width / 14)) }, () => ({
      x: Math.random() * c.width,
      y: Math.random() * c.height * 0.55,
      s: Math.random() > 0.9 ? 2 : 1,
    }));
    flies = Array.from({ length: Math.min(36, Math.floor(c.width / 40)) }, makeFly);

    // blocky skyline: flat-roofed houses of varying width and height,
    // y quantized so the silhouette reads as pixel art
    houses = [];
    wins = [];
    let x = -10;
    while (x < c.width + 10) {
      const w = 40 + ((Math.random() * 70) | 0);
      const y = Math.round((c.height * (0.82 + Math.random() * 0.1)) / 4) * 4;
      houses.push({ x, w, y });
      // a sparse grid of windows per house; each blinks on its own clock
      const cols = Math.floor(w / (STEP * 3));
      const rows = Math.floor((c.height - y) / (STEP * 3));
      for (let i = 0; i < cols; i++)
        for (let j = 0; j < Math.min(rows, 4); j++)
          if (Math.random() < 0.4)
            wins.push({
              x: x + 6 + i * STEP * 3,
              y: y + 6 + j * STEP * 3,
              lit: Math.random() < 0.5,
              next: Math.random() * 14,
            });
      x += w + 4 + ((Math.random() * 14) | 0);
    }
  };
  seed();
  addEventListener("resize", seed, { signal });

  const draw = () => {
    ctx.clearRect(0, 0, c.width, c.height);
    for (const st of stars) {
      ctx.globalAlpha = 0.25 + Math.random() * 0.45;
      ctx.fillStyle = "#cdd8e2";
      ctx.fillRect(st.x | 0, st.y | 0, st.s, st.s);
    }
    ctx.globalAlpha = 1;
    for (const h of houses) {
      ctx.fillStyle = "rgba(16, 11, 26, 0.95)";
      ctx.fillRect(h.x, h.y, h.w, c.height - h.y);
      // a faint violet edge along each roofline
      ctx.fillStyle = "rgba(177, 140, 255, 0.16)";
      ctx.fillRect(h.x, h.y, h.w, 2);
    }
    for (const w of wins) {
      if (!w.lit) continue;
      ctx.fillStyle = "rgba(177, 140, 255, 0.55)";
      ctx.fillRect(w.x, w.y, STEP, STEP);
    }
    for (const f of flies) {
      ctx.globalAlpha = 0.25 + 0.55 * (0.5 + 0.5 * Math.sin(f.phase));
      ctx.fillStyle = f.col;
      ctx.fillRect(f.x | 0, f.y | 0, 2, 2);
    }
    ctx.globalAlpha = 1;
  };

  if (REDUCED) {
    draw();
    return;
  }
  let t = 0;
  const tick = () => {
    if (signal.aborted) return;
    t += 0.016;
    for (const w of wins)
      if (t > w.next) {
        w.lit = !w.lit;
        w.next = t + 4 + Math.random() * 16;
      }
    for (const f of flies) {
      f.x += f.vx + Math.sin(t * 0.5 + f.phase) * 0.15;
      f.y += f.vy + Math.cos(t * 0.4 + f.phase) * 0.1;
      f.phase += 0.03;
      if (f.x < -4 || f.x > c.width + 4 || f.y < c.height * 0.35 || f.y > c.height + 4)
        Object.assign(f, makeFly());
    }
    draw();
    requestAnimationFrame(tick);
  };
  tick();
}

/* ───────── the eve machine ───────── */
function eveMachine(signal: AbortSignal) {
  const c = document.querySelector<HTMLCanvasElement>("#eve-px")!;
  const ctx = c.getContext("2d")!;
  const S = 16;
  const px = c.width / S;

  // d outline · s shell · S panel · g glass · e eye glow · L led slot
  const map = [
    "................",
    "...dddddddddd...",
    "..dssssssssssd..",
    "..dsggggggggsd..",
    "..dsgg.ee.ggsd..",
    "..dsg.eeee.gsd..",
    "..dsg.eeee.gsd..",
    "..dsgg.ee.ggsd..",
    "..dsggggggggsd..",
    "..dssssssssssd..",
    "..dsSSSSSSSSsd..",
    "..dsSLSSSSLSsd..",
    "..dsSSSSSSSSsd..",
    "..dssssssssssd..",
    "...dd......dd...",
    "................",
  ];
  const colors: Record<string, string> = {
    d: "#241640",
    s: "#7a5fc4",
    S: "#4a3582",
    g: "#140e24",
    e: "#b18cff",
    L: "#2bd9ff",
  };
  // when the eye "sleeps" it collapses to a content horizontal line —
  // the eavesdropper, off duty
  const EYE_OPEN: Array<[number, number]> = [
    [7, 4], [8, 4],
    [6, 5], [7, 5], [8, 5], [9, 5],
    [6, 6], [7, 6], [8, 6], [9, 6],
    [7, 7], [8, 7],
  ];
  const EYE_SHUT: Array<[number, number]> = [
    [6, 5], [7, 5], [8, 5], [9, 5],
  ];

  const drawSprite = (eyeShut: boolean, ledOn: boolean) => {
    ctx.fillStyle = "#0a0714";
    ctx.fillRect(0, 0, c.width, c.height);
    for (let y = 0; y < S; y++)
      for (let x = 0; x < S; x++) {
        const ch = map[y]?.[x] ?? ".";
        if (ch === "." || ch === "e") continue;
        if (ch === "L") {
          ctx.fillStyle = ledOn ? colors.L! : "#1b3947";
        } else {
          ctx.fillStyle = colors[ch] ?? "#b18cff";
        }
        ctx.fillRect(x * px, y * px, px, px);
      }
    ctx.fillStyle = colors.e!;
    for (const [x, y] of eyeShut ? EYE_SHUT : EYE_OPEN) ctx.fillRect(x * px, y * px, px, px);
  };

  drawSprite(true, true);
  if (REDUCED) return;

  // the eye stays shut; every few seconds it peeks open for a beat,
  // remembers its manners, and closes again. the led heartbeats.
  const PEEK_MS = 260;
  const CYCLE_MS = 4200;
  let start = performance.now() + 2000;
  const tick = (now: number) => {
    if (signal.aborted) return;
    const t = now - start;
    if (t > CYCLE_MS) start = now;
    const peeking = t > 0 && t < PEEK_MS;
    const ledOn = Math.floor(now / 900) % 2 === 0;
    drawSprite(!peeking, ledOn);
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

onPage((signal) => {
  village(signal);
  eveMachine(signal);
});
